import {
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from "@angular/core";
import { FormControl, Validators } from "@angular/forms";
import { NgSelectComponent } from "@ng-select/ng-select";
import { concat, Observable, of, Subject } from "rxjs";
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    map,
    startWith,
    switchMap,
    tap
} from "rxjs/operators";
import { AutocompleteService } from "../../../services/autocomplete.service";

@Component({
    selector: "app-select-http-input",
    templateUrl: "./select-http-input.component.html",
    styleUrls: ["./select-http-input.component.scss"]
})
export class SelectHttpInputComponent implements OnInit, OnChanges, OnDestroy {
    @ViewChild(NgSelectComponent, { static: false }) select: NgSelectComponent;

    input$ = new Subject<string>();
    control = new FormControl();

    @Input() url: string;
    @Input() placeholder = "";
    @Input() labelField: string;
    @Input() valueField: string;
    @Input() requiredMessage = "";
    @Input() value;
    @Input() disabled;
    @Output() valueChange = this.control.valueChanges.pipe(
        map(item => item?.value),
        distinctUntilChanged()
    );

    isLoading = false;
    items$: Observable<any>;

    unsubscribe$ = new Subject();

    constructor(private autocompleteService: AutocompleteService) {}

    ngOnInit() {
        this.items$ = concat(
            of([]),
            this.input$.pipe(
                startWith(""),
                distinctUntilChanged(),
                debounceTime(400),
                tap(() => (this.isLoading = true)),
                switchMap(value => this._filter(value))
            )
        );

        if (this.requiredMessage) {
            this.control.setValidators(Validators.required);
        }
    }

    ngOnChanges() {
        if (this.control.value?.value === this.value || !this.value) return;
        this.getValueById();
    }

    async getValueById() {
        const v = await this.autocompleteService
            .findByIds(this.url, this.value)
            .toPromise();
        const value = (v || [])[0] || {};
        this.control.setValue({
            label: value[this.labelField],
            value: value[this.valueField]
        });

        if (this.disabled) {
            this.control.disable();
        } else {
            this.control.enable();
        }
    }

    ngOnDestroy() {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    private _filter(value: string) {
        const filterValue = (value || "").toLowerCase();
        return this.autocompleteService
            .search(this.url, filterValue, this.valueField, this.labelField)
            .pipe(
                map(val =>
                    val.map(item => ({
                        label: item[this.labelField],
                        value: item[this.valueField]
                    }))
                ),
                catchError(() => of([])),
                tap(() => (this.isLoading = false))
            );
    }
}
